/**
 * Responsibility: split a card's completed sales into the grades they were in.
 *
 * The catalogue's sold table is every condition at once. Measured on
 * Charizard #4: of 376 sales, 58 were grade 10, 35 raw, 30 at 9.5 and the
 * rest spread from 1 to 9. Drawn as one line that is not a price history -
 * it is three markets on top of each other, and the average across them
 * belongs to none of the three.
 *
 * Buckets are Raw, then each grade from 8 up, then everything below 8 in
 * one. Eight is where the graded market starts paying a premium worth
 * plotting, and a chip per half-grade from 1 to 7 would be twenty controls
 * nobody uses. Nothing is hidden: the low grades keep a bucket of their
 * own rather than being dropped, and "All" is still there for the shape of
 * the whole market.
 */
import type { SoldSale } from '../../api/client';

/** Every sale, whatever condition. */
export const ALL = 'ALL';

/** Ungraded copies: the title named no grader. */
export const RAW = 'RAW';

/** Graded, but below where the premium starts. Kept so nothing vanishes. */
export const LOW = 'LOW';

/** Where a grade starts being worth its own plot. */
const PREMIUM_FROM = 8;

export type SaleBucket = {
  /** `ALL`, `RAW`, `LOW`, or the grade as written: "8", "9.5", "10". */
  key: string;
  label: string;
  count: number;
};

/** Which bucket one sale belongs in. */
export function bucketOf(sale: SoldSale): string {
  if (!sale.is_graded) return RAW;
  // A slab whose title names a grader but no number cannot be placed on a
  // grade, and calling it raw would be wrong in the other direction.
  if (sale.grade_label === null) return LOW;
  const grade = Number(sale.grade_label);
  if (!Number.isFinite(grade)) return LOW;
  return grade >= PREMIUM_FROM ? sale.grade_label : LOW;
}

function labelFor(key: string): string {
  if (key === RAW) return 'Raw';
  if (key === LOW) return 'Under 8';
  return `Grade ${key}`;
}

/**
 * The buckets that actually have sales, in the order they should be shown.
 *
 * Only what exists: a chip for a grade this card has never sold at is a
 * control that can only ever show an empty chart.
 */
export function bucketsOf(sales: SoldSale[]): SaleBucket[] {
  const counts = new Map<string, number>();
  for (const sale of sales) {
    const key = bucketOf(sale);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const graded = [...counts.keys()]
    .filter((key) => key !== RAW && key !== LOW)
    .sort((left, right) => Number(right) - Number(left));

  const ordered = [RAW, ...graded, LOW].filter((key) => counts.has(key));
  const buckets = ordered.map((key) => ({
    key,
    label: labelFor(key),
    count: counts.get(key) ?? 0,
  }));

  // "All" earns its place only when there is more than one thing to
  // combine; on a card that only ever sold raw it would be a duplicate.
  if (buckets.length > 1) {
    buckets.push({ key: ALL, label: 'All', count: sales.length });
  }
  return buckets;
}

export function salesIn(sales: SoldSale[], bucket: string): SoldSale[] {
  if (bucket === ALL) return sales;
  return sales.filter((sale) => bucketOf(sale) === bucket);
}

/**
 * Which bucket to open on.
 *
 * `preferred` is the grade the page is already about - the grade a holding
 * is in, or the grade a card's headline price came from - because that is
 * the question the operator already asked. It is only honoured when that
 * grade actually has sales; otherwise the busiest bucket is the most
 * informative thing to show first.
 */
export function defaultBucket(buckets: SaleBucket[], preferred?: string | null): string {
  if (buckets.length === 0) return ALL;

  if (preferred) {
    const wanted = /(\d+(?:\.\d)?)/.exec(preferred);
    const isRaw = /ungraded|raw/i.test(preferred);
    const key = isRaw ? RAW : wanted?.[1];
    const found = buckets.find((bucket) => bucket.key === key);
    if (found) return found.key;
  }

  const busiest = buckets
    .filter((bucket) => bucket.key !== ALL)
    .reduce((best, bucket) => (bucket.count > best.count ? bucket : best));
  return busiest.key;
}
